import {FormInstance} from 'antd/es/form';
import {pick} from 'lodash-es';

import {
    EReportPropertiesFieldTypes,
    ReportPropertiesSettingsFieldsResetDependencies,
    EReportPropertiesSettingsFormFields,
} from '../constants/report-properties-settings';

export const ReportPropertiesDefaultValueToTypeMap = {
    [EReportPropertiesFieldTypes.INTEGER]: EReportPropertiesSettingsFormFields.defaultValueInteger,
    [EReportPropertiesFieldTypes.NUMBER]: EReportPropertiesSettingsFormFields.defaultValueInteger,
    [EReportPropertiesFieldTypes.STRING]: EReportPropertiesSettingsFormFields.defaultValueString,
    [EReportPropertiesFieldTypes.DATE]: EReportPropertiesSettingsFormFields.defaultValueDate,
    [EReportPropertiesFieldTypes.DATETIME]: EReportPropertiesSettingsFormFields.defaultValueDateTime,
    [EReportPropertiesFieldTypes.BOOLEAN]: EReportPropertiesSettingsFormFields.defaultValueBoolean,
    [EReportPropertiesFieldTypes.VALUE_SET]: EReportPropertiesSettingsFormFields.defaultValueValueSet,
    [EReportPropertiesFieldTypes.MULTI_VALUE_SET]: EReportPropertiesSettingsFormFields.defaultValueMultiValueSet,
    [EReportPropertiesFieldTypes.SQLVALUESET]: EReportPropertiesSettingsFormFields.defaultValueSqlValueSet,
    [EReportPropertiesFieldTypes.SQLMULTIVALUESET]: EReportPropertiesSettingsFormFields.defaultValueSqlValueSet,
    [EReportPropertiesFieldTypes.VALUERANGE]: EReportPropertiesSettingsFormFields.defaultValueDateRange,
};

const reportPropertiesDefaultValueMapper = (values: any) => {
    if (!values.defaultType || values.defaultType === 'UNSET') return undefined;

    if (values?.defaultType === 'CONTEXT') {
        return values?.defaultValueContext;
    }
    const field = ReportPropertiesDefaultValueToTypeMap[values?.type as EReportPropertiesFieldTypes];

    if (field && values?.type === EReportPropertiesFieldTypes.MULTI_VALUE_SET) {
        if (Array.isArray(values?.[field])) {
            return values[field].join(', ');
        }
        return undefined;
    }

    if (field && values?.type === EReportPropertiesFieldTypes.VALUERANGE) {
        if (Array.isArray(values?.[field])) {
            return JSON.stringify(values[field]);
        }
        return undefined;
    }

    if (field) return values?.[field];
    return undefined;
};

export const convertPropertiesFormDataToRequestData = (values: any) => {
    if (!values) return values;
    if (!values && !Array.isArray(values) && !values?.length) return [];
    const dtoMappedValues = values.map((v: any) => ({
        ...v,
        defaultValue: reportPropertiesDefaultValueMapper(v),
        defaultType: v.defaultType === 'UNSET' ? undefined : v.defaultType,
    }));
    return dtoMappedValues.map((v: any) => pick(v, Object.values(EReportPropertiesSettingsFormFields)));
};

export const getDataFromReportPropertiesSettingsRequest = (values: unknown[]) => values.map((v: any) => {
    const defaultValue = (() => {
        if (!v.defaultType || v.defaultType === 'UNSET') return undefined;
        return v.defaultValue;
    })();

    return {
        ...v,
        [ReportPropertiesDefaultValueToTypeMap[v.type as EReportPropertiesFieldTypes]]: defaultValue,
        defaultValue,
        defaultValueContext: v.defaultType === 'CONTEXT' ? defaultValue : undefined,
        defaultValueMultiValueSet: (() => {
            if (v.type !== 'MULTIVALUESET') return undefined;
            if (v.defaultType === 'CONTEXT') return defaultValue;
            if (typeof defaultValue === 'string') defaultValue?.split(', ');
            if (Array.isArray(defaultValue)) return v.defaultValue;
            return undefined;
        })(),
        defaultValueDateRange: (() => {
            if (v.type !== 'VALUERANGE') return undefined;
            if (v.defaultType === 'CONTEXT') return defaultValue;
            if (typeof defaultValue === 'string') {
                try {
                    return JSON.parse(defaultValue);
                } catch {
                    return undefined;
                }
            }
            if (Array.isArray(defaultValue)) return defaultValue;
            return undefined;
        })(),
    };
});

export const resetReportPropertiesSettingsFormFields = (
    form: FormInstance,
    fieldName: number,
    field: keyof typeof ReportPropertiesSettingsFieldsResetDependencies,
) => {
    const {queryParameters} = form.getFieldsValue();
    const queryParametersCopy = [...queryParameters];

    queryParametersCopy[fieldName] = {
        ...queryParameters[fieldName],
        ...(ReportPropertiesSettingsFieldsResetDependencies[field].reduce((acc, curr) => ({
            ...acc,
            [curr]: curr === EReportPropertiesSettingsFormFields.defaultType ? 'UNSET' : undefined,
        }), {})),
    };
    form.setFieldsValue({
        queryParameters: queryParametersCopy,
    });
};
